import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarBlank, Check, X, PencilSimple, Trash, Plus, PaperPlaneTilt, ClipboardText, DownloadSimple } from "@phosphor-icons/react";

const STATUS_BADGE = {
  pending: "bg-warning/10 text-warning border-warning/20",
  approved: "bg-success/10 text-success border-success/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

const emptyRow = () => ({ tag: "", date: "", client_loc: "", activities: "" });

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(`${d}T00:00:00`);
  if (isNaN(dt.getTime())) return d;
  return `${dt.getDate()} ${dt.toLocaleDateString("en-US", { month: "short" })}`;
};

function toCsv(headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
}
function downloadFile(name, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

const COLS = "grid grid-cols-[minmax(120px,150px)_90px_minmax(0,1fr)_minmax(0,1fr)]";

function ActivityBlock({ employeeName, rows }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden text-sm">
      <div className={`${COLS} bg-primary/10 border-b border-border`}>
        <div className="font-heading font-bold px-3 py-2 border-r border-border">{employeeName}</div>
        <div className="font-semibold px-3 py-2 border-r border-border text-center">Date</div>
        <div className="font-semibold px-3 py-2 border-r border-border text-center">Client/Loc</div>
        <div className="font-semibold px-3 py-2 text-center">Activities</div>
      </div>
      {(rows || []).map((e, i) => (
        <div key={i} className={`${COLS} border-b border-border last:border-b-0`}>
          <div className="px-3 py-1.5 border-r border-border text-primary font-medium text-xs uppercase tracking-wide">{e.tag || ""}</div>
          <div className="px-3 py-1.5 border-r border-border text-center">{fmtDate(e.date)}</div>
          <div className="px-3 py-1.5 border-r border-border text-center">{e.client_loc || ""}</div>
          <div className="px-3 py-1.5 text-center">{e.activities || ""}</div>
        </div>
      ))}
    </div>
  );
}

function ScheduleCard({ a, canApprove, canEdit, isAdmin, onDecide, onEdit, onDelete, onPdf }) {
  return (
    <Card className="p-5 border-border shadow-none space-y-4 animate-fade-up" data-testid={`act-card-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-bold text-lg">{a.employee_name}</h3>
            <Badge variant="outline" className={`capitalize font-medium ${STATUS_BADGE[a.status]}`}>{a.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {a.title ? `${a.title} · ` : ""}Plotted by {a.created_by_name} · {a.rows?.length || 0} {a.rows?.length === 1 ? "day" : "days"}
          </p>
          {a.decided_by_name && (
            <p className="text-xs text-muted-foreground mt-1">
              {a.status === "approved" ? "Approved" : "Rejected"} by {a.decided_by_name}{a.decision_note ? ` — ${a.decision_note}` : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => onPdf(a)} data-testid={`act-pdf-${a.id}`} className="gap-1.5">
            <DownloadSimple className="h-3.5 w-3.5" /> PDF
          </Button>
          {canApprove && a.status === "pending" && (
            <>
              <Button size="sm" onClick={() => onDecide(a, "approved")} data-testid={`act-approve-${a.id}`} className="gap-1.5 bg-success hover:bg-success/90 text-white font-semibold">
                <Check className="h-3.5 w-3.5" /> Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDecide(a, "rejected")} data-testid={`act-reject-${a.id}`} className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10">
                <X className="h-3.5 w-3.5" /> Reject
              </Button>
            </>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => onEdit(a)} data-testid={`act-edit-${a.id}`} className="gap-1.5">
              <PencilSimple className="h-3.5 w-3.5" /> Edit
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => onDelete(a)} data-testid={`act-delete-${a.id}`} className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10">
              <Trash className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      <ActivityBlock employeeName={a.employee_name} rows={a.rows} />
    </Card>
  );
}

export default function ActivitySchedule() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canApprove = user?.role === "admin" || user?.role === "manager";
  const canCreate = user?.role === "admin" || user?.role === "supervisor";
  const canEdit = user?.role === "admin" || user?.role === "supervisor";

  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeId, setEmployeeId] = useState("");
  const [title, setTitle] = useState("");
  const [rows, setRows] = useState([emptyRow()]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/activities");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, [load]);

  const updateRow = (i, patch) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (i) => setRows((rs) => rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs);

  const submit = async () => {
    if (!employeeId) { toast.error("Select an employee for this schedule."); return; }
    const valid = rows.filter((r) => r.date);
    if (!valid.length) { toast.error("Add at least one row with a date."); return; }
    setBusy(true);
    try {
      await api.post("/activities", { employee_id: employeeId, title, rows: valid });
      toast.success("Activity schedule submitted for approval");
      setRows([emptyRow()]);
      setTitle("");
      setEmployeeId("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (a, status) => {
    try {
      await api.put(`/activities/${a.id}/decision`, { status });
      toast.success(`Activity schedule ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({ employee_id: a.employee_id || "", title: a.title || "", rows: (a.rows || []).map((r) => ({ ...r })) });
  };
  const editRow = (i, patch) => setForm((f) => ({ ...f, rows: f.rows.map((r, idx) => idx === i ? { ...r, ...patch } : r) }));
  const saveEdit = async () => {
    try {
      await api.put(`/activities/${editing.id}`, {
        employee_id: form.employee_id, title: form.title,
        rows: form.rows.map((r) => ({ tag: r.tag, date: r.date, client_loc: r.client_loc, activities: r.activities })),
      });
      toast.success("Activity schedule updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/activities/${confirmDel.id}`);
      toast.success("Activity schedule deleted");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadPdf = async (a) => {
    try {
      const res = await api.get(`/activities/${a.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Activity_${(a.employee_name || "schedule").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const pending = items.filter((a) => a.status === "pending");
  const decided = items.filter((a) => a.status !== "pending");

  const downloadCsv = () => {
    if (!items.length) { toast.error("No schedules to download"); return; }
    const headers = ["Title", "Employee", "Plotted By", "Status", "Tag", "Date", "Client/Loc", "Activities"];
    const out = [];
    items.forEach((a) => (a.rows || []).forEach((r) => out.push([
      a.title, a.employee_name, a.created_by_name, a.status, r.tag, r.date, r.client_loc, r.activities])));
    downloadFile(`activity-schedule-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, out));
  };

  return (
    <div className="space-y-6" data-testid="activity-schedule-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shrink-0">
            <CalendarBlank weight="duotone" className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Schedules</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Activity Schedule</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {canCreate ? "Plot one employee's monthly activities and submit for approval." : "View plotted activities and approvals."}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={downloadCsv} data-testid="act-download-csv" className="gap-1.5">
          <DownloadSimple className="h-4 w-4" /> Download CSV
        </Button>
      </div>

      {canCreate && (
        <Card className="p-6 border-border shadow-none space-y-5" data-testid="act-plot-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Employee</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger data-testid="act-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Schedule title (optional)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. January Field Schedule" data-testid="act-title" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="min-w-[150px]">Tag</TableHead>
                  <TableHead className="min-w-[150px]">Date</TableHead>
                  <TableHead className="min-w-[200px]">Client/Loc</TableHead>
                  <TableHead className="min-w-[240px]">Activities</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} data-testid={`act-row-${i}`}>
                    <TableCell><Input value={r.tag} onChange={(e) => updateRow(i, { tag: e.target.value })} placeholder="ON CALL / REST DAY" data-testid={`act-row-tag-${i}`} /></TableCell>
                    <TableCell><Input type="date" value={r.date} onChange={(e) => updateRow(i, { date: e.target.value })} data-testid={`act-row-date-${i}`} /></TableCell>
                    <TableCell><Input value={r.client_loc} onChange={(e) => updateRow(i, { client_loc: e.target.value })} placeholder="Client / Location" data-testid={`act-row-loc-${i}`} /></TableCell>
                    <TableCell><Input value={r.activities} onChange={(e) => updateRow(i, { activities: e.target.value })} placeholder="Activities" data-testid={`act-row-activities-${i}`} /></TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => removeRow(i)} data-testid={`act-row-remove-${i}`} className="text-destructive h-8 w-8">
                        <Trash className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button variant="outline" onClick={addRow} data-testid="act-add-row" className="gap-1.5">
              <Plus className="h-4 w-4" /> Add row
            </Button>
            <Button onClick={submit} disabled={busy} data-testid="act-submit" className="gap-1.5 font-semibold">
              <PaperPlaneTilt className="h-4 w-4" /> {busy ? "Submitting..." : "Submit for approval"}
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : (
        <>
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <ClipboardText weight="duotone" className="h-5 w-5 text-warning" />
              <h2 className="font-heading text-lg font-bold">Pending approval</h2>
              <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 font-semibold" data-testid="act-pending-count">{pending.length}</Badge>
            </div>
            {pending.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity schedules awaiting approval.</p>
            ) : pending.map((a) => (
              <ScheduleCard key={a.id} a={a} canApprove={canApprove} canEdit={canEdit} isAdmin={isAdmin} onDecide={decide} onEdit={openEdit} onDelete={setConfirmDel} onPdf={downloadPdf} />
            ))}
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-lg font-bold">Records</h2>
            {decided.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity records yet.</p>
            ) : decided.map((a) => (
              <ScheduleCard key={a.id} a={a} canApprove={canApprove} canEdit={canEdit} isAdmin={isAdmin} onDecide={decide} onEdit={openEdit} onDelete={setConfirmDel} onPdf={downloadPdf} />
            ))}
          </section>
        </>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="act-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit activity schedule</DialogTitle>
            <DialogDescription>Plotted by {editing?.created_by_name}</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Employee</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger data-testid="act-edit-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="act-edit-title" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead>Tag</TableHead><TableHead>Date</TableHead><TableHead>Client/Loc</TableHead><TableHead>Activities</TableHead><TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.rows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell><Input value={r.tag} onChange={(e) => editRow(i, { tag: e.target.value })} placeholder="ON CALL / REST DAY" /></TableCell>
                        <TableCell><Input type="date" value={r.date} onChange={(e) => editRow(i, { date: e.target.value })} /></TableCell>
                        <TableCell><Input value={r.client_loc} onChange={(e) => editRow(i, { client_loc: e.target.value })} /></TableCell>
                        <TableCell><Input value={r.activities} onChange={(e) => editRow(i, { activities: e.target.value })} /></TableCell>
                        <TableCell><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setForm((f) => ({ ...f, rows: f.rows.filter((_, idx) => idx !== i) }))}><Trash className="h-4 w-4" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setForm((f) => ({ ...f, rows: [...f.rows, emptyRow()] }))}>
                <Plus className="h-4 w-4" /> Add row
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="act-edit-save" className="font-semibold">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent data-testid="act-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive">Delete activity schedule?</DialogTitle>
            <DialogDescription>This permanently removes {confirmDel?.employee_name}'s schedule. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="act-delete-confirm" className="gap-1.5"><Trash className="h-4 w-4" /> Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
